import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import '../../../../src/i18n.js';
import { SUPPORTED_LANGUAGES } from '../../../../src/i18n.js';
import { Conversation } from '../../conversation.js';
import { Exam } from '../../exam.js';

const DEFAULT_LNG = 'en';

/** Resolve an i18n code from a stored conversation language, which may be a code ("de") or a label
 * ("Deutsch") — pure/sync variant of resolveConversationLanguage below, for callers that already have the
 * raw language string in hand and don't need to fetch it from the DB. */
export function resolveLngCode(language?: string | null): string {
  const v = String(language ?? '')
    .trim()
    .toLowerCase();
  if (!v) return DEFAULT_LNG;
  const byValue = SUPPORTED_LANGUAGES.find(l => l.value === v);
  if (byValue) return byValue.value;
  const byLabel = SUPPORTED_LANGUAGES.find(l => l.label.toLowerCase() === v);
  return byLabel ? byLabel.value : DEFAULT_LNG;
}

export async function resolveConversationLanguage(params: {
  conversationId?: string;
  examId?: string;
}): Promise<string> {
  let { conversationId, examId } = params;
  try {
    if (!conversationId) {
      const examCol = await getCollection(Exam);
      const exam = await examCol.findOne({ _id: examId }, { projection: { conversationId: 1 } as any });
      conversationId = String((exam as any)?.conversationId ?? '').trim();
    }
    const convCol = await getCollection(Conversation);
    let conv: any | null = null;
    if (conversationId) {
      conv = await convCol.findOne({ _id: conversationId }, { projection: { language: 1 } as any });
    }
    if (!conv && examId) {
      const examCol = await getCollection(Exam);
      const exam = await examCol.findOne({ _id: examId }, { projection: { conversationId: 1 } as any });
      const cId = String((exam as any)?.conversationId ?? '').trim();
      if (cId) conv = await convCol.findOne({ _id: cId }, { projection: { language: 1 } as any });
    }
    const raw = String((conv as any)?.language ?? '').trim();
    const languageValue = SUPPORTED_LANGUAGES.find(lang => lang.label === raw)?.value;
    const available = SUPPORTED_LANGUAGES.map(language => String(language.value).toLowerCase());
    logger.info(
      { raw, languageValue, availableLanguages: available.join(', '), conversationId, examId },
      'Resolved conversation language',
    );
    if (available.includes(languageValue as any) && raw) return languageValue as string;
    logger.warn(
      { conversationId, examId, raw, available },
      'Conversation language not found or not supported, defaulting to German',
    );
    return 'de';
  } catch (e) {
    return 'de';
  }
}
